import EventBus from '@/utilities/eventBus';
import hideDropdownsMixin from '@/mixins/hideDropdownsMixin';
import { ResourceType } from '@/types/resources';

export default {
  mixins: [hideDropdownsMixin],
  data() {
    return {
      cardViewportCheckTimout: null,
      loadPageTimeout: null,
      sprintLoaded: false,
      boardLoaded: false,
    };
  },
  computed: {
    boardOrEpicsBoard() {
      return [this.$constants.routeNames.board, this.$constants.epicsBoardRoute.name];
    },
    teamId() {
      return this.$store.getters.getTeamID;
    },
    mouseSuspended() {
      return this.$store.getters.boardViewMouseSuspended;
    },
    projects() {
      return this.$store.getters.getProjects;
    },
    boardsAndProjectsLoaded() {
      return Object.keys(this.board).length > 0 && this.projects.length > 0;
    },
    sidebarExpanded() {
      return this.$store.getters.sidebarExpanded;
    },
    isPreviousRouteRoot() {
      return this.$store.getters.isPreviousRouteRoot;
    },
    cardForEdit() {
      return this.$store.getters.getCurrentCardActionEdit;
    },
    cardMembersForEdit() {
      return this.$store.getters.getCurrentCardMembersDropdown;
    },
    cardMemberIdForEdit() {
      return this.$store.getters.getCurrentCardIdMemberDropdown;
    },
    initialScrollX() {
      return this.$route.meta.scrollPositions?.[this.$route.params?.boardId] || 0;
    },
    isSprint() {
      return this.$route.name === this.$constants.routeNames.sprint;
    },
  },
  methods: {
    dragStart(event) {
      const wrapper = document.createElement('div');
      wrapper.className = 'drag-list-rows-wrap';
      const list = document.querySelector('.sortable-drag .draggable-cards');
      if (list) {
        const maxHeight =
          this.$store.getters.getBoardListView === 'grid'
            ? list.clientHeight - (list.firstChild.clientHeight + list.lastChild.clientHeight)
            : list.clientHeight;

        wrapper.style.height = `${maxHeight}px`;
        list.appendChild(wrapper);
        let height = 0;

        const cards = document.querySelectorAll('.sortable-drag .draggable-cards .list-row');
        [].forEach.call(cards, (item) => {
          if (height < maxHeight) {
            let elHeight = item.offsetHeight;
            elHeight += parseInt(window.getComputedStyle(item).getPropertyValue('margin-top'), 10);
            elHeight += parseInt(
              window.getComputedStyle(item).getPropertyValue('margin-bottom'),
              10
            );
            height += elHeight;
          }
          wrapper.appendChild(item);
        });
        wrapper.style.height = `${height}px`;

        const scrollPanel = event.item.querySelector('.__panel');
        if (scrollPanel) {
          const scrollPosition = scrollPanel.scrollTop;
          wrapper.scrollTop = scrollPosition;
        }
      }
      const { listId } = event.item.dataset;
      this.$store.dispatch('setDraggedEntity', {
        entityName: 'list',
        id: listId,
      });
    },
    dragEnd() {
      this.$store.dispatch('setDraggedEntity', {});
      document.body.style.cursor = 'default';
    },
    checkBoardListMove(e) {
      return !!e.relatedContext.element;
    },
    loadSprintById(id) {
      if (!id) return Promise.resolve();
      const [projectId, sprintId] = id.split('s');

      return this.$store
        .dispatch('getSprint', { projectId, sprintId })
        .then(({ sprint }) => {
          this.setCurrentProjectId(sprint.project_id);
          this.sprintLoaded = true;
        })
        .catch(() => {})
        .finally(() => {
          if (this.isPreviousRouteRoot) {
            this.loadPageTimeout = setTimeout(() => {
              this.$nextTick(() => {
                this.onPageLoad();
              });
            }, 200);
          }
          this.scrollToInitialPosition();
        });
    },

    loadBoardById(boardId) {
      if (!boardId) return Promise.resolve();

      // allow loading a board in the backgorund
      // if (boardId === this.$store.getters.currentBoardId) return;
      return this.$store
        .dispatch('fetchBoard', { boardId })
        .then(({ board }) => {
          // added because of mixing options api with composition api (double script)
          this.boardLoaded = true;
          const embed = this.embed ?? this._setupProxy?.embed.value;
          if (!embed) {
            this.setCurrentProjectId(board.project_id);
          }
          if (!board.lists.length && board.title !== '' && board.title != null) {
            this.$store.dispatch('setDraftListInputVisibleOnOpen', true);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (this.isPreviousRouteRoot) {
            this.loadPageTimeout = setTimeout(() => {
              this.$nextTick(() => {
                this.onPageLoad();
              });
            }, 200);
          }
          this.scrollToInitialPosition();
        });
    },
    hideCardEdit() {
      this.$store.dispatch('setCurrentCardIdForActionEdit', '');
      this.$store.dispatch('setCurrentCardIdMemberDropdown', '');
      this.$store.dispatch('setCurrentCardMembersDropdown', {});
      this.$store.dispatch('setCurrentCardIdForPopup', '');
    },
    setHiglightedCard(cardId, listId) {
      this.$store.dispatch('setSelectedCardId', cardId);
      this.$store.dispatch('lastHoveredCardId', cardId);
      this.$store.dispatch('lastHoveredListId', listId);
      // scroll list into view
      EventBus.$emit('list:checkIsInViewport', listId);

      // scroll card into view after a short timeout to ensure cards are fully rendered
      if (this.cardViewportCheckTimout) clearTimeout(this.cardViewportCheckTimout);
      this.cardViewportCheckTimout = setTimeout(() => {
        EventBus.$emit('card:checkIsInViewport', cardId);
        clearTimeout(this.cardViewportCheckTimout);
      }, 100);
    },
    updateBoardInBoards(newOrder) {
      this.$store.dispatch('updateBoardPropertyInBoards', {
        boardId: this.currentBoard.id,
        propertyName: 'list_order',
        value: newOrder,
      });
    },
    onCreated(
      boardIdParam = this.$route.params?.boardId ||
        this.embeddedBoardAttributes?.boardId ||
        this._setupProxy?.embeddedBoardAttributes.boardId.value
    ) {
      const boardId =
        this.$route.name === this.$constants.epicsBoardRoute.name
          ? this.$constants.epicsBoardId
          : boardIdParam;

      if (!boardId) return;

      if (boardId.includes(this.$constants.inEpicBoardIdPrefix)) {
        const epicId = boardId.split('-')[1];
        this.$store.dispatch('fetchCard', { cardId: epicId, type: ResourceType.Epic }).then(() => {
          this.$store.dispatch('constructInEpicBoard', epicId);
        });
        return;
      }

      // added because of mixing options api with composition api (double script)
      const embed = this.embed ?? this._setupProxy?.embed.value;
      if (boardId !== this.$store.getters.currentBoardId && !embed) {
        this.$store.dispatch('setCurrentBoardId', boardId);
      }
      const currentBoard = this.$store.getters.getFlatBoards[boardId];
      if (
        (this.boardOrEpicsBoard.includes(this.$route.name) && !currentBoard?.localBoard) ||
        (embed && !this.isSprint)
      ) {
        this.loadBoardById(boardId);
      } else if (this.isSprint) {
        this.loadSprintById(boardId);
      }
      this.$socket?.subscribe?.(this.isSprint ? 'sprints' : 'boards', boardId);
      if (this.$route.name === 'newboard') {
        this.$nextTick(() => {
          this.onPageLoad();
        });
      }
    },
    addFirstListIds() {
      if (!this.boardListsIds) return;
      this.visibleListIds = [];
      const listIds = [];
      for (let i = 0; i < this.maxDisplayedLists; i += 1) {
        if (this.boardListsIds[i]) {
          listIds.push(this.boardListsIds[i]);
        }
      }
      this.visibleListIds = listIds;
    },
    setListsIdsWatcher() {
      if (!this.boardListsIds) return;
      if (this.boardListsIds.length) {
        // if listIds are already loaded then add first few to visible listIds
        this.addFirstListIds();
      } else {
        // if listIds are not loaded yet then add listener to add them to visible listIds
        this.idsWatch = this.$watch('boardListsIds.length', () => {
          this.addFirstListIds();
          this.idsWatch();
        });
      }
    },
    setCurrentProjectId(projectId) {
      const project = this.$store.getters.getMyProjectById(projectId);
      if (project) this.$store.dispatch('setCurrentProjectId', projectId);
    },
    scrollToInitialPosition() {
      if (this.initialScrollX !== 0 || this.$route.meta?.popstate) {
        this.$refs.boardView?.scrollTo(
          {
            x: this.initialScrollX,
          },
          0
        );
      }
    },
  },
  unmounted() {
    if (this.$store.getters.boardViewNoDestroy) return;
    // close card actions dropdown
    this.hideCardEdit();
    if (this.cardViewportCheckTimout) clearTimeout(this.cardViewportCheckTimout);
    clearTimeout(this.loadPageTimeout);
  },
  watch: {
    boardsAndProjectsLoaded(loaded) {
      if (!loaded) return;

      this.setCurrentProjectId(this.board.project_id);
    },
    $route(to, from) {
      this.hideCardActionsDropdown();
      this.hideMemberDropdown();

      if (
        this.boardOrEpicsBoard.includes(to.name) &&
        this.boardOrEpicsBoard.includes(from.name) &&
        from.params.boardId !== to.params.boardId &&
        from.params.boardId !== to.params.boardId
      ) {
        if (this.$refs.boardView && !this.initialScrollX && !to.meta.popstate) {
          this.$refs.boardView?.scrollTo(
            {
              x: '0%',
            },
            0
          );
        }
        this.setListsIdsWatcher();
        // close card actions dropdown
        this.hideCardEdit();
      }
    },
    '$route.params.boardId': {
      handler(newVal, oldVal) {
        // added because of mixing options api with composition api (double script)
        const embed = this.embed ?? this._setupProxy?.embed.value;
        if (!newVal && !embed) return;
        // newVal === ResourceType.Sprints is for the case when we are in board sprint settings
        if (
          (!newVal && this.embeddedBoardAttributes?.boardType === ResourceType.Sprint) ||
          newVal === ResourceType.Sprints
        )
          return;
        if (
          (newVal === oldVal || newVal === this.board.id) &&
          this.boardOrEpicsBoard.includes(this.$route.name)
        )
          return;
        this.onCreated();
      },
      deep: true,
      immediate: true,
    },
  },
};
